// src/page/admin/WelcomeCardTemplates.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import TableComponent from "../../components/TableComponent";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import {
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };

// const FONT_TNR = `"Times New Roman", Times, serif`;
const FONT_TNR = "";
const BASE_MD_WIDTH = 768;

/* ---------- Permission helpers ---------- */
const isSuperAdmin = () => String(localStorage.getItem("userId")) === "1";
const getUserPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions");
    return raw ? JSON.parse(raw).map((p) => p.permissionSlug) : [];
  } catch {
    return [];
  }
};
const hasPermission = (slug) =>
  isSuperAdmin() || getUserPermissions().includes(slug);

/* ---------- UI primitives ---------- */
const Input = (props) => (
  <input
    {...props}
    className={`input input-bordered w-full ${props.className || ""}`}
  />
);
const Select = (props) => (
  <select
    {...props}
    className={`select select-bordered w-full ${props.className || ""}`}
  />
);

const Modal = ({ isOpen, title, onClose, children, footer }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="relative bg-white w-full max-w-7xl mx-2 rounded-2xl p-6 shadow-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            title="Đóng"
          >
            ✕
          </button>
        </div>
        <div className="space-y-4">{children}</div>
        {footer && <div className="mt-6">{footer}</div>}
      </div>
    </div>
  );
};

/* ---------- ScaledStage ---------- */
function useScaledStage(baseWidth = BASE_MD_WIDTH) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [frameSize, setFrameSize] = useState({ w: baseWidth, h: 0 });

  const update = () => {
    const availW = outerRef.current?.clientWidth || baseWidth;
    const s = Math.min(1, availW / baseWidth);
    setScale(s);

    if (innerRef.current) {
      const h = innerRef.current.offsetHeight || 0;
      setFrameSize({ w: baseWidth * s, h: h * s });
    }
  };

  useLayoutEffect(() => {
    update();

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (outerRef.current && ro) ro.observe(outerRef.current);
    if (innerRef.current && ro) ro.observe(innerRef.current);

    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseWidth]);

  return { outerRef, innerRef, scale, frameSize };
}

function ScaledStage({ children }) {
  const { outerRef, innerRef, scale, frameSize } = useScaledStage();

  return (
    <div ref={outerRef} style={{ width: "100%" }}>
      <div
        style={{
          width: frameSize.w,
          height: frameSize.h,
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        <div
          ref={innerRef}
          style={{
            width: BASE_MD_WIDTH,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const toPct = (px, base) => (base ? (px / base) * 100 : 0);

const safeParseSchema = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
};

const genId = (prefix = "layer") =>
  `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;

/* =========================================================
   DEFAULT_SCHEMA (V2) — bám chuẩn thiệp mẫu gốc
========================================================= */
const DEFAULT_SCHEMA = {
  schemaVersion: 2,
  backgroundSrc: "/welcome_user_template2.jpg",
  elements: {
    header: { top: 3, left: 0, width: 100, height: 8 },
    welcome: { top: 9, left: 0, width: 100, height: 16 },
    member: { top: 27, left: 0, width: 100, height: 14 },
    centerLogo: { top: 40, left: 0, width: 100, height: 18 },
    avatar: { top: 55, left: 15, width: 18, height: 18 },
    repInfo: { top: 56, left: 42, width: 55, height: 14, fontSize: 18 },
    companyInfo: { top: 72, left: 10, width: 80, height: 18, fontSize: 18 },
    footer: { bottom: 4, left: 0, width: 100, height: 10, fontSize: 18 },
  },
  assets: {
    topLogoSrc: "/logo192.png",
    centerLogoSrc: "/logo_welcome_v2.png",
  },
  memberBlock: {
    logoW: 160,
    logoH: 90,
    memberFontSize: 25,
  },
  texts: {
    headerLine1: "Hội Xây Dựng",
    headerLine2: "THÀNH PHỐ CẦN THƠ",
    welcomeTitle: "CHÀO MỪNG",
    memberLine1: "Là Hội viên chính thức của",
    memberLine2: "Hội Xây Dựng TP. Cần Thơ",
    repTitle: "NGƯỜI ĐẠI DIỆN THAM GIA",
    companyTitle: "THÔNG TIN DOANH NGHIỆP:",
    footerTitle: "SẢN PHẨM, DỊCH VỤ KINH DOANH:",
  },
  layers: [],
};

const mergeSchema = (loaded) => {
  const s = loaded || {};
  return {
    ...DEFAULT_SCHEMA,
    ...s,
    elements: { ...DEFAULT_SCHEMA.elements, ...(s.elements || {}) },
    assets: { ...DEFAULT_SCHEMA.assets, ...(s.assets || {}) },
    memberBlock: { ...DEFAULT_SCHEMA.memberBlock, ...(s.memberBlock || {}) },
    texts: { ...DEFAULT_SCHEMA.texts, ...(s.texts || {}) },
    layers: Array.isArray(s.layers) ? s.layers : [],
    schemaVersion: 2,
  };
};

/* ---------- Template vars (token + preview) ---------- */
const TEMPLATE_VARS = {
  companyName: { token: "{{companyName}}", preview: "[công ty]" },
  establishedDate: {
    token: "{{establishedDate}}",
    preview: "[ngày thành lập]",
  },
  businessAddress: { token: "{{businessAddress}}", preview: "[địa chỉ]" },
  companyPhone: { token: "{{companyPhone}}", preview: "[sđt doanh nghiệp]" },
  companyEmail: { token: "{{companyEmail}}", preview: "[email doanh nghiệp]" },
  websiteOrHotline: {
    token: "{{websiteOrHotline}}",
    preview: "[website/hotline]",
  },
  businessField: { token: "{{businessField}}", preview: "[sản phẩm/dịch vụ]" },

  repUserFullName: {
    token: "{{repUserFullName}}",
    preview: "[người đại diện]",
  },
  repUserPosition: { token: "{{repUserPosition}}", preview: "[chức vụ]" },
  repUserPhone: { token: "{{repUserPhone}}", preview: "[sđt người đại diện]" },
  repUserEmail: {
    token: "{{repUserEmail}}",
    preview: "[email người đại diện]",
  },
};

const renderTemplatePreviewText = (text = "") =>
  String(text || "").replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (m, key) => {
    return TEMPLATE_VARS[key]?.preview ?? m;
  });

/* ---------- Mock preview ---------- */
const MOCK = {
  company: {
    companyName: TEMPLATE_VARS.companyName.preview,
    businessAddress: TEMPLATE_VARS.businessAddress.preview,
    userPhone: TEMPLATE_VARS.companyPhone.preview,
    userEmail: TEMPLATE_VARS.companyEmail.preview,
    websiteOrHotline: TEMPLATE_VARS.websiteOrHotline.preview,
    businessField: TEMPLATE_VARS.businessField.preview,
    establishedDate: TEMPLATE_VARS.establishedDate.preview,
    logoUrl: "",
    userAvatar: "",
    logo: "",
    companyAvatar: "",
    avatar: "",
  },
  repUser: {
    userFullName: TEMPLATE_VARS.repUserFullName.preview,
    userPhone: TEMPLATE_VARS.repUserPhone.preview,
    userEmail: TEMPLATE_VARS.repUserEmail.preview,
    userAvatar: "",
    CompaniesHasUsers: { userPosition: TEMPLATE_VARS.repUserPosition.preview },
  },
};

const pickLogo = (c) =>
  c?.userAvatar || c?.logo || c?.companyAvatar || c?.avatar || c?.logoUrl || "";

const fallbackAvatarUrl = (() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
    <rect width="100%" height="100%" rx="100" fill="#0ea5e9"/>
    <text x="50%" y="55%" text-anchor="middle" font-size="80" fill="#fff">U</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
})();

/* ---------- Fields ---------- */
const FIELD_DEFS = [
  {
    key: "rep.userFullName",
    label: "Người đại diện: Họ tên",
    prefix: "Người đại diện: ",
  },
  {
    key: "rep.userPosition",
    label: "Người đại diện: Chức vụ",
    prefix: "Chức vụ: ",
  },
  { key: "rep.userPhone", label: "Người đại diện: SĐT", prefix: "SĐT: " },
  { key: "rep.userEmail", label: "Người đại diện: Email", prefix: "Email: " },

  {
    key: "company.companyName",
    label: "Doanh nghiệp: Tên công ty",
    prefix: "",
  },
  {
    key: "company.establishedDate",
    label: "Doanh nghiệp: Ngày thành lập",
    prefix: "Ngày thành lập: ",
  },
  {
    key: "company.businessAddress",
    label: "Doanh nghiệp: Địa chỉ",
    prefix: "Địa chỉ: ",
  },
  {
    key: "company.userPhone",
    label: "Doanh nghiệp: SĐT",
    prefix: "Điện thoại: ",
  },
  { key: "company.userEmail", label: "Doanh nghiệp: Email", prefix: "Email: " },
  {
    key: "company.websiteOrHotline",
    label: "Doanh nghiệp: Website/Hotline",
    prefix: "Website: ",
  },
  {
    key: "company.businessField",
    label: "Doanh nghiệp: Ngành nghề",
    prefix: "",
  },
];

const resolveFieldValue = (key) => {
  switch (key) {
    case "rep.userFullName":
      return TEMPLATE_VARS.repUserFullName.token;
    case "rep.userPosition":
      return TEMPLATE_VARS.repUserPosition.token;
    case "rep.userPhone":
      return TEMPLATE_VARS.repUserPhone.token;
    case "rep.userEmail":
      return TEMPLATE_VARS.repUserEmail.token;

    case "company.companyName":
      return TEMPLATE_VARS.companyName.token;
    case "company.establishedDate":
      return TEMPLATE_VARS.establishedDate.token;
    case "company.businessAddress":
      return TEMPLATE_VARS.businessAddress.token;
    case "company.userPhone":
      return TEMPLATE_VARS.companyPhone.token;
    case "company.userEmail":
      return TEMPLATE_VARS.companyEmail.token;
    case "company.websiteOrHotline":
      return TEMPLATE_VARS.websiteOrHotline.token;
    case "company.businessField":
      return TEMPLATE_VARS.businessField.token;
    default:
      return "—";
  }
};

const IMAGE_SOURCES = [
  { key: "", label: "Nhập URL ảnh (tự do)" },
  { key: "asset.topLogoSrc", label: "Assets: topLogoSrc" },
  { key: "asset.centerLogoSrc", label: "Assets: centerLogoSrc" },
  { key: "company.logo", label: "Doanh nghiệp: Logo" },
  { key: "rep.avatar", label: "Người đại diện: Avatar" },
];

const resolveImageSource = (key, schema, company, repUser) => {
  if (!key) return "";
  if (key === "asset.topLogoSrc") return schema?.assets?.topLogoSrc || "";
  if (key === "asset.centerLogoSrc") return schema?.assets?.centerLogoSrc || "";
  if (key === "company.logo") return pickLogo(company) || "";
  if (key === "rep.avatar") return repUser?.userAvatar || fallbackAvatarUrl;
  return "";
};

/* ---------- normalize (V2) ----------
  QUY ƯỚC V2 (đồng bộ MemberEditor/UserPdfModal):
  - top/bottom: % theo CHIỀU CAO thiệp
  - left/width: % theo CHIỀU RỘNG thiệp
  - height: % theo CHIỀU CAO thiệp (circle image sẽ 1:1 theo width khi render)
-------------------------------------- */
function normalizeLayer(layer) {
  const type = layer?.type === "image" ? "image" : "text";
  const style = layer?.style || {};

  const out = {
    id: layer?.id || genId(type === "image" ? "img" : "txt"),
    type,

    left: Number(layer?.left ?? 0),
    width: Number(layer?.width ?? 20),
    height: Number(layer?.height ?? 6),
    zIndex: Number(layer?.zIndex ?? 10),

    top: layer?.bottom != null ? undefined : Number(layer?.top ?? 0),
    bottom: layer?.bottom != null ? Number(layer?.bottom) : undefined,

    text: type === "text" ? String(layer?.text ?? "") : undefined,

    src: type === "image" ? String(layer?.src ?? "") : undefined,
    fit:
      type === "image"
        ? layer?.fit === "contain"
          ? "contain"
          : "cover"
        : undefined,
    circle: type === "image" ? !!layer?.circle : undefined,
    sourceKey: type === "image" ? String(layer?.sourceKey ?? "") : undefined,

    style:
      type === "text"
        ? {
            fontFamily: style.fontFamily ?? FONT_TNR,
            fontSize: Number(style.fontSize ?? 18),
            fontWeight: String(style.fontWeight ?? "400"),
            color: style.color ?? "#ffffff",
            align: style.align ?? "left",
            uppercase: !!style.uppercase,
            lineHeight: Number(style.lineHeight ?? 1.25),
          }
        : undefined,
  };

  if (out.type === "image" && out.circle) out.height = out.width; // lưu 1:1
  return out;
}

function ensureSchemaV2(loaded) {
  const s = mergeSchema(loaded);
  s.schemaVersion = 2;

  if (Array.isArray(s.layers) && s.layers.length) {
    s.layers = s.layers
      .map((l) => normalizeLayer(l))
      .sort((a, b) => Number(a.zIndex) - Number(b.zIndex));
    return s;
  }

  const el = s.elements || DEFAULT_SCHEMA.elements;
  const t = s.texts || DEFAULT_SCHEMA.texts;

  const mkText = (patch) =>
    normalizeLayer({
      type: "text",
      ...patch,
      style: {
        fontFamily: patch?.style?.fontFamily ?? FONT_TNR,
        fontSize: patch?.style?.fontSize ?? 18,
        fontWeight: patch?.style?.fontWeight ?? "600",
        color: patch?.style?.color ?? "#ffffff",
        align: patch?.style?.align ?? "center",
        uppercase: patch?.style?.uppercase ?? false,
        lineHeight: patch?.style?.lineHeight ?? 1.25,
      },
    });

  const mkImg = (patch) =>
    normalizeLayer({
      type: "image",
      ...patch,
      fit: patch?.fit ?? "contain",
      circle: !!patch?.circle,
      sourceKey: patch?.sourceKey ?? "",
      src: patch?.src ?? "",
    });

  const layers = [];

  // ===== HEADER group (logo 50 + gap 12 + text 320) =====
  const logoPx = 50;
  const gapPx = 12;
  const textPx = 320;
  const groupW = logoPx + gapPx + textPx;

  const groupLeftPct = ((BASE_MD_WIDTH - groupW) / 2 / BASE_MD_WIDTH) * 100;
  const logoLeftPct = groupLeftPct;
  const textLeftPct =
    (((BASE_MD_WIDTH - groupW) / 2 + logoPx + gapPx) / BASE_MD_WIDTH) * 100;
  const textWPct = (textPx / BASE_MD_WIDTH) * 100;

  layers.push(
    mkImg({
      id: "img_topLogo",
      top: el.header?.top ?? 3,
      left: logoLeftPct,
      width: (logoPx / BASE_MD_WIDTH) * 100,
      height: (logoPx / BASE_MD_WIDTH) * 100,
      zIndex: 20,
      sourceKey: "asset.topLogoSrc",
      fit: "contain",
    }),
  );

  layers.push(
    mkText({
      id: "txt_header1",
      top: (el.header?.top ?? 3) + 0.1,
      left: textLeftPct,
      width: textWPct,
      height: 3.0,
      zIndex: 21,
      text: t.headerLine1,
      style: {
        fontSize: 18,
        fontWeight: "600",
        align: "center",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_header2",
      top: (el.header?.top ?? 3) + 2.2,
      left: textLeftPct,
      width: textWPct,
      height: 3.0,
      zIndex: 21,
      text: t.headerLine2,
      style: {
        fontSize: 18,
        fontWeight: "600",
        align: "center",
        fontFamily: FONT_TNR,
      },
    }),
  );

  // ===== WELCOME =====
  const padPx = 56;
  const welcomeLeftPct = (padPx / BASE_MD_WIDTH) * 100;
  const welcomeWidthPct = 100 - welcomeLeftPct * 2;

  layers.push(
    mkText({
      id: "txt_welcomeTitle",
      top: el.welcome?.top ?? 9,
      left: 0,
      width: 100,
      height: 5.5,
      zIndex: 22,
      text: t.welcomeTitle,
      style: {
        fontSize: 38,
        fontWeight: "400",
        align: "center",
        fontFamily: FONT_TNR,
      },
    }),
  );

  layers.push(
    mkText({
      id: "txt_companyName",
      top: (el.welcome?.top ?? 9) + 5.2,
      left: welcomeLeftPct,
      width: welcomeWidthPct,
      height: 8.0,
      zIndex: 23,
      text: TEMPLATE_VARS.companyName.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 27,
        fontWeight: "700",
        color: "#facc15",
        align: "center",
        uppercase: true,
        lineHeight: 1.18,
      },
    }),
  );

  // ===== MEMBER block =====
  const logoW = s.memberBlock?.logoW ?? 160;
  const logoH = s.memberBlock?.logoH ?? 90;
  layers.push(
    mkImg({
      id: "img_companyLogo",
      top: el.member?.top - 3 ?? 24,
      left: ((BASE_MD_WIDTH - logoW) / 2 / BASE_MD_WIDTH) * 100,
      width: (logoW / BASE_MD_WIDTH) * 100,
      height: (logoH / BASE_MD_WIDTH) * 100,
      zIndex: 24,
      sourceKey: "company.logo",
      fit: "contain",
    }),
  );

  layers.push(
    mkText({
      id: "txt_member1",
      top: (el.member?.top ?? 27) + 9.8,
      left: 0,
      width: 100,
      height: 4.2,
      zIndex: 25,
      text: t.memberLine1,
      style: {
        fontSize: s.memberBlock?.memberFontSize ?? 25,
        fontWeight: "600",
        align: "center",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_member2",
      top: (el.member?.top ?? 27) + 13.1,
      left: 0,
      width: 100,
      height: 4.2,
      zIndex: 25,
      text: t.memberLine2,
      style: {
        fontSize: s.memberBlock?.memberFontSize ?? 25,
        fontWeight: "600",
        align: "center",
        fontFamily: FONT_TNR,
      },
    }),
  );

  // ===== CENTER LOGO =====
  const centerW = 122;
  const centerH = 150;
  layers.push(
    mkImg({
      id: "img_centerLogo",
      top: el.centerLogo?.top ?? 43,
      left: ((BASE_MD_WIDTH - centerW) / 2 / BASE_MD_WIDTH) * 100,
      width: (centerW / BASE_MD_WIDTH) * 100,
      height: (centerH / BASE_MD_WIDTH) * 100,
      zIndex: 26,
      sourceKey: "asset.centerLogoSrc",
      fit: "contain",
    }),
  );

  // ===== AVATAR =====
  const avW = el.avatar?.width ?? 18;
  layers.push(
    mkImg({
      id: "img_repAvatar",
      top: el.avatar?.top ?? 55,
      left: el.avatar?.left ?? 15,
      width: avW,
      height: avW,
      zIndex: 27,
      sourceKey: "rep.avatar",
      fit: "cover",
      circle: true,
    }),
  );

  // ===== REP INFO =====
  const repLeft = el.repInfo?.left ?? 42;
  const repTop = el.repInfo?.top ?? 56;
  const repW = el.repInfo?.width ?? 55;
  const repFs = el.repInfo?.fontSize ?? 18;

  layers.push(
    mkText({
      id: "txt_repTitle",
      top: repTop,
      left: repLeft,
      width: repW,
      height: 4.2,
      zIndex: 28,
      text: t.repTitle,
      style: {
        fontSize: repFs,
        fontWeight: "700",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_repName",
      top: repTop + 3.9,
      left: repLeft,
      width: repW,
      height: 3.2,
      zIndex: 28,
      text: `• Người đại diện: ${TEMPLATE_VARS.repUserFullName.token}`,
      style: {
        fontSize: repFs,
        fontWeight: "300",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_repPos",
      top: repTop + 6.8,
      left: repLeft,
      width: repW,
      height: 3.2,
      zIndex: 28,
      text: `• Chức vụ: ${TEMPLATE_VARS.repUserPosition.token}`,
      style: {
        fontSize: repFs,
        fontWeight: "300",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_repPhone",
      top: repTop + 9.7,
      left: repLeft,
      width: repW,
      height: 3.2,
      zIndex: 28,
      text: `• SĐT: ${TEMPLATE_VARS.repUserPhone.token}`,
      style: {
        fontSize: repFs,
        fontWeight: "300",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_repEmail",
      top: repTop + 12.6,
      left: repLeft,
      width: repW,
      height: 3.2,
      zIndex: 28,
      text: `• Email: ${TEMPLATE_VARS.repUserEmail.token}`,
      style: {
        fontSize: repFs,
        fontWeight: "300",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );

  // ===== COMPANY INFO =====
  const cLeft = el.companyInfo?.left ?? 10;
  const cTop = el.companyInfo?.top ?? 70;
  const cW = el.companyInfo?.width ?? 80;
  const cFs = el.companyInfo?.fontSize ?? 18;

  layers.push(
    mkText({
      id: "txt_companyTitle",
      top: cTop,
      left: cLeft,
      width: cW,
      height: 4.2,
      zIndex: 29,
      text: t.companyTitle,
      style: {
        fontSize: cFs,
        fontWeight: "700",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyDate",
      top: cTop + 3.9,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `• Ngày thành lập: ${TEMPLATE_VARS.establishedDate.token}`,
      style: {
        fontSize: cFs,
        fontWeight: "300",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyAddr",
      top: cTop + 6.8,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `• Địa chỉ: ${TEMPLATE_VARS.businessAddress.token}`,
      style: {
        fontSize: cFs,
        fontWeight: "300",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyPhone",
      top: cTop + 9.7,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `• Điện thoại: ${TEMPLATE_VARS.companyPhone.token}`,
      style: {
        fontSize: cFs,
        fontWeight: "300",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyEmail",
      top: cTop + 12.6,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `• Email: ${TEMPLATE_VARS.companyEmail.token}`,
      style: {
        fontSize: cFs,
        fontWeight: "300",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyWeb",
      top: cTop + 15.5,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `• Website: ${TEMPLATE_VARS.websiteOrHotline.token}`,
      style: {
        fontSize: cFs,
        fontWeight: "300",
        align: "left",
        fontFamily: FONT_TNR,
      },
    }),
  );

  // ===== FOOTER (bottom %) =====
  const fBottom = el.footer?.bottom ?? 4;
  const fW = el.footer?.width ?? 100;
  const fLeft = el.footer?.left ?? 0;
  const fFs = el.footer?.fontSize ?? 18;

  layers.push(
    normalizeLayer({
      id: "txt_footerTitle",
      type: "text",
      bottom: fBottom,
      left: fLeft,
      width: fW,
      height: 4.0,
      zIndex: 30,
      text: t.footerTitle,
      style: {
        fontFamily: FONT_TNR,
        fontSize: fFs,
        fontWeight: "700",
        color: "#fff",
        align: "center",
        uppercase: false,
        lineHeight: 1.25,
      },
    }),
  );

  layers.push(
    normalizeLayer({
      id: "txt_footerValue",
      type: "text",
      bottom: fBottom - 3,
      left: fLeft,
      width: fW,
      height: 4.0,
      zIndex: 30,
      text: TEMPLATE_VARS.businessField.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: fFs,
        fontWeight: "300",
        color: "#fff",
        align: "center",
        uppercase: false,
        lineHeight: 1.25,
      },
    }),
  );

  s.layers = layers
    .map((l) => normalizeLayer(l))
    .sort((a, b) => Number(a.zIndex) - Number(b.zIndex));

  return s;
}

/* ---------- Render layers ---------- */
function LayerText({ layer }) {
  const st = layer.style || {};
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        fontFamily: st.fontFamily ?? FONT_TNR,
        fontSize: st.fontSize ?? 18,
        fontWeight: st.fontWeight ?? "400",
        color: st.color ?? "#fff",
        textAlign: st.align ?? "left",
        lineHeight: st.lineHeight ?? 1.25,
        textTransform: st.uppercase ? "uppercase" : "none",
        whiteSpace: "pre-wrap",
        overflow: "hidden",
      }}
    >
      {renderTemplatePreviewText(layer.text || "")}
    </div>
  );
}

function LayerImage({ layer, resolvedSrc }) {
  const src = resolvedSrc || layer.src || "";
  const circle = !!layer.circle;

  return (
    <div
      data-bg-src={src}
      style={{
        width: "100%",
        height: "100%",
        borderRadius: circle ? "9999px" : "12px",
        overflow: "hidden",
        backgroundColor: "transparent",
        backgroundImage: src ? `url("${src}")` : "none",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: layer.fit === "contain" ? "contain" : "cover",
      }}
    />
  );
}

/**
 * Render V2 đồng bộ MemberEditor/UserPdfModal:
 * - left/width: % theo WIDTH -> px theo BASE_MD_WIDTH
 * - top/bottom: % theo HEIGHT -> px theo cardHeight (đo từ ảnh nền)
 * - height: % theo HEIGHT (cardHeight) nếu có, fallback theo WIDTH nếu chưa đo được
 */
function PageSingleEditable({
  schema,
  previewData,
  editorMode = false,
  onCompanyNameEl,
  cardHeight,
  onCardHeightChange,
}) {
  const s = ensureSchemaV2(schema);
  const company = previewData?.company;
  const repUser = previewData?.repUser;
  const layers = s.layers || [];

  const bgImgRef = useRef(null);

  const handleBgLoad = () => {
    const h = bgImgRef.current?.offsetHeight || 0;
    onCardHeightChange?.(h);
  };

  useLayoutEffect(() => {
    if (!bgImgRef.current) return;
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            const h = bgImgRef.current?.offsetHeight || 0;
            onCardHeightChange?.(h);
          })
        : null;
    if (ro) ro.observe(bgImgRef.current);
    return () => ro?.disconnect?.();
  }, [onCardHeightChange]);

  const cardH = Number(cardHeight || 0);

  return (
    <div className="relative" style={{ width: BASE_MD_WIDTH }}>
      <img
        ref={bgImgRef}
        src={s.backgroundSrc || DEFAULT_SCHEMA.backgroundSrc}
        className="block w-full h-auto"
        alt=""
        draggable={false}
        onLoad={handleBgLoad}
      />

      {layers
        .slice()
        .sort((a, b) => Number(a.zIndex ?? 1) - Number(b.zIndex ?? 1))
        .map((layer) => {
          const hasBottom = layer.bottom !== undefined && layer.bottom !== null;

          const leftPx = (BASE_MD_WIDTH * Number(layer.left ?? 0)) / 100;
          const widthPx = (BASE_MD_WIDTH * Number(layer.width ?? 10)) / 100;

          const heightPx =
            layer.type === "image" && layer.circle
              ? widthPx
              : cardH
                ? (cardH * Number(layer.height ?? 6)) / 100
                : (BASE_MD_WIDTH * Number(layer.height ?? 6)) / 100;

          const topPx =
            !hasBottom && cardH ? (cardH * Number(layer.top ?? 0)) / 100 : 0;
          const bottomPx =
            hasBottom && cardH ? (cardH * Number(layer.bottom ?? 0)) / 100 : 0;

          const wrapperStyle = {
            position: "absolute",
            left: `${leftPx}px`,
            width: `${widthPx}px`,
            height: `${heightPx}px`,
            zIndex: layer.zIndex ?? 1,
            pointerEvents: editorMode ? "none" : "auto",
            ...(hasBottom
              ? { bottom: `${bottomPx}px` }
              : { top: `${topPx}px` }),
          };

          if (layer.type === "image") {
            const resolvedSrc = resolveImageSource(
              layer.sourceKey,
              s,
              company,
              repUser,
            );
            return (
              <div key={layer.id} style={wrapperStyle}>
                <LayerImage layer={layer} resolvedSrc={resolvedSrc} />
              </div>
            );
          }

          const isCompanyNameLayer = layer.id === "txt_companyName";
          return (
            <div
              key={layer.id}
              style={wrapperStyle}
              ref={
                isCompanyNameLayer ? (el) => onCompanyNameEl?.(el) : undefined
              }
            >
              <LayerText layer={layer} />
            </div>
          );
        })}
    </div>
  );
}

/* ---------- Drag overlay ---------- */
function useDrag({ onStart, onMove, onEnd }) {
  const draggingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });

  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    draggingRef.current = true;
    startRef.current = { x: e.clientX, y: e.clientY };

    onStart?.(e);

    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    e.preventDefault();

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    startRef.current = { x: e.clientX, y: e.clientY };

    onMove?.({ dx, dy });
  };

  const onPointerUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    onEnd?.(e);
  };

  return { onPointerDown, onPointerMove, onPointerUp };
}

/**
 * LayerBox render theo px đồng bộ MemberEditor/UserPdfModal
 * - left/width: % chiều rộng -> px (BASE_MD_WIDTH)
 * - top/bottom: % chiều cao -> px (cardHeight đo theo ảnh nền)
 * - height: % chiều cao (cardHeight), circle 1:1 theo width
 */
const LayerBox = ({ layer, selected, onSelect, onDrag, cardHeight }) => {
  const hasBottom = layer.bottom !== undefined && layer.bottom !== null;
  const isCircleImg = layer.type === "image" && !!layer.circle;

  const leftPx = (BASE_MD_WIDTH * Number(layer.left ?? 0)) / 100;
  const widthPx = (BASE_MD_WIDTH * Number(layer.width ?? 10)) / 100;

  const heightPx = isCircleImg
    ? widthPx
    : cardHeight
      ? (cardHeight * Number(layer.height ?? 6)) / 100
      : (BASE_MD_WIDTH * Number(layer.height ?? 6)) / 100;

  const topPx =
    !hasBottom && cardHeight ? (cardHeight * Number(layer.top ?? 0)) / 100 : 0;

  const bottomPx =
    hasBottom && cardHeight
      ? (cardHeight * Number(layer.bottom ?? 0)) / 100
      : 0;

  const drag = useDrag({
    onStart: () => onSelect?.(layer.id),
    onMove: onDrag,
  });

  return (
    <div
      className="absolute"
      style={{
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        top: !hasBottom ? `${topPx}px` : undefined,
        bottom: hasBottom ? `${bottomPx}px` : undefined,
        zIndex: 9999,
      }}
      {...drag}
    >
      <div
        className="absolute inset-0"
        style={{
          border: selected
            ? "2px solid #3b82f6"
            : "1px dashed rgba(255,255,255,0.55)",
          borderRadius: 10,
          background: selected ? "rgba(59,130,246,0.10)" : "rgba(0,0,0,0.02)",
          cursor: "move",
          touchAction: "none",
          userSelect: "none",
        }}
      />
      {selected && (
        <div
          className="absolute -top-7 left-0 px-2 py-1 text-xs rounded"
          style={{ background: "#3b82f6", color: "white" }}
        >
          {layer.type === "image" ? "Ảnh" : "Text"} · {layer.id}
        </div>
      )}
    </div>
  );
};

/* ---------- Template editor stage ---------- */
function TemplateEditorStage({ schema, setSchema, selected, setSelected }) {
  const cardRef = useRef(null);

  const s = ensureSchemaV2(schema);
  const layers = s.layers || [];

  const [cardHeight, setCardHeight] = useState(0);

  const updateLayer = (id, patch) => {
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      next.layers = (next.layers || []).map((l) => {
        if (l.id !== id) return l;
        const merged = normalizeLayer({ ...l, ...patch });
        return merged;
      });
      return next;
    });
  };

  const updateByDrag = (id, { dx, dy }) => {
    const base = cardRef.current;
    if (!base) return;

    const rect = base.getBoundingClientRect();
    const baseW = rect.width; // scaled width
    const baseH = rect.height; // scaled height

    const cur = layers.find((x) => x.id === id);
    if (!cur) return;

    const dLeftPct = toPct(dx, baseW);
    const dTopPct = toPct(dy, baseH);

    const nextLeft = clamp((cur.left ?? 0) + dLeftPct, 0, 100);

    if (cur.bottom !== undefined && cur.bottom !== null) {
      const nextBottom = clamp((cur.bottom ?? 0) - dTopPct, 0, 100);
      updateLayer(id, { left: nextLeft, bottom: nextBottom });
    } else {
      const nextTop = clamp((cur.top ?? 0) + dTopPct, 0, 100);
      updateLayer(id, { left: nextLeft, top: nextTop });
    }
  };

  return (
    <div className="w-full" onClick={() => setSelected(null)}>
      <ScaledStage>
        <div
          ref={cardRef}
          className="relative"
          style={{ width: BASE_MD_WIDTH }}
          onClick={(e) => e.stopPropagation()}
        >
          <PageSingleEditable
            schema={s}
            previewData={MOCK}
            editorMode
            cardHeight={cardHeight}
            onCardHeightChange={(h) => setCardHeight(h)}
          />

          {layers.map((layer) => (
            <LayerBox
              key={layer.id}
              layer={layer}
              selected={selected === layer.id}
              onSelect={setSelected}
              onDrag={(d) => updateByDrag(layer.id, d)}
              cardHeight={cardHeight}
            />
          ))}
        </div>
      </ScaledStage>
    </div>
  );
}

/* ---------- Right panel controls ---------- */
function ElementControls({ schema, setSchema, selected, setSelected }) {
  const s = ensureSchemaV2(schema);
  const layers = s.layers || [];
  const layer = layers.find((l) => l.id === selected);

  const [quickFieldKey, setQuickFieldKey] = useState(
    FIELD_DEFS[0]?.key || "rep.userFullName",
  );
  const [selectedFieldKey, setSelectedFieldKey] = useState(
    FIELD_DEFS[0]?.key || "rep.userFullName",
  );

  useEffect(() => {
    setSelectedFieldKey(FIELD_DEFS[0]?.key || "rep.userFullName");
  }, [selected]);

  const setLayer = (patch) => {
    if (!selected) return;
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      next.layers = (next.layers || []).map((l) => {
        if (l.id !== selected) return l;
        const merged = normalizeLayer({ ...l, ...patch });
        return merged;
      });
      return next;
    });
  };

  const deleteLayer = () => {
    if (!selected) return;
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      next.layers = (next.layers || []).filter((l) => l.id !== selected);
      return next;
    });
    setSelected?.(null);
  };

  const insertFieldAsText = () => {
    const def =
      FIELD_DEFS.find((x) => x.key === quickFieldKey) || FIELD_DEFS[0];
    const value = resolveFieldValue(def.key, MOCK.company, MOCK.repUser);

    const newLayer = normalizeLayer({
      id: genId("txt"),
      type: "text",
      top: 60,
      left: 10,
      width: 60,
      height: 4,
      zIndex: (layers?.length || 0) + 50,
      text: `${def.prefix || ""}${value}`,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 18,
        fontWeight: "400",
        color: "#ffffff",
        align: "left",
        uppercase: false,
        lineHeight: 1.25,
      },
    });

    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      next.layers = [...(next.layers || []), newLayer];
      return next;
    });

    setSelected?.(newLayer.id);
    toast.info("Đã chèn thông tin (text có thể sửa).");
  };

  const applySelectedFromField = (fieldKey) => {
    if (!layer || layer.type !== "text") return;
    const def = FIELD_DEFS.find((x) => x.key === fieldKey);
    if (!def) return;
    const value = resolveFieldValue(def.key, MOCK.company, MOCK.repUser);
    setLayer({ text: `${def.prefix || ""}${value}` });
    toast.info("Đã auto điền nội dung (bạn có thể sửa tiếp).");
  };

  const st = layer?.type === "text" ? layer.style || {} : null;
  const hasBottom = layer?.bottom !== undefined && layer?.bottom !== null;
  const isCircleImg = layer?.type === "image" && !!layer?.circle;

  if (!selected || !layer) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-gray-500">
          Chọn một khối trên thiệp để chỉnh (click vào khung nét đứt).
        </div>

        <div className="border-t pt-4 space-y-2">
          <div className="font-semibold">Chèn thông tin</div>
          <Select
            value={quickFieldKey}
            onChange={(e) => setQuickFieldKey(e.target.value)}
          >
            {FIELD_DEFS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </Select>
          <button
            className="btn btn-outline btn-primary w-full"
            onClick={insertFieldAsText}
          >
            <PlusIcon className="w-5 h-5 mr-1" />
            Chèn vào thiệp
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold">
          Đang chỉnh:{" "}
          <span className="text-blue-600">{layer.type.toUpperCase()}</span>
        </div>
        <button
          className="btn btn-outline btn-error btn-sm"
          onClick={deleteLayer}
          title="Xoá khối này"
        >
          <TrashIcon className="w-4 h-4 mr-1" />
          Xoá
        </button>
      </div>

      {/* Position / size */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="label">
            <span className="label-text">Left (%)</span>
          </div>
          <Input
            type="number"
            value={layer.left ?? 0}
            onChange={(e) => setLayer({ left: Number(e.target.value) })}
          />
        </div>

        {!hasBottom ? (
          <div>
            <div className="label">
              <span className="label-text">Top (%)</span>
            </div>
            <Input
              type="number"
              value={layer.top ?? 0}
              onChange={(e) => setLayer({ top: Number(e.target.value) })}
            />
          </div>
        ) : (
          <div>
            <div className="label">
              <span className="label-text">Bottom (%)</span>
            </div>
            <Input
              type="number"
              value={layer.bottom ?? 0}
              onChange={(e) => setLayer({ bottom: Number(e.target.value) })}
            />
          </div>
        )}

        <div className="col-span-2">
          <div className="label">
            <span className="label-text">Width (% theo chiều rộng)</span>
          </div>
          <Input
            type="number"
            value={layer.width ?? 20}
            onChange={(e) => {
              const w = Number(e.target.value);
              if (isCircleImg) setLayer({ width: w, height: w });
              else setLayer({ width: w });
            }}
          />
        </div>

        <div className="col-span-2">
          <div className="label">
            <span className="label-text">Height (% theo chiều cao)</span>
          </div>
          <Input
            type="number"
            value={isCircleImg ? (layer.width ?? 20) : (layer.height ?? 6)}
            disabled={isCircleImg}
            onChange={(e) => setLayer({ height: Number(e.target.value) })}
          />
          {isCircleImg && (
            <div className="text-xs text-gray-500 mt-1">
              Ảnh bo tròn luôn 1:1 theo Width.
            </div>
          )}
        </div>

        <div className="col-span-2">
          <div className="label">
            <span className="label-text">Z-index</span>
          </div>
          <Input
            type="number"
            value={layer.zIndex ?? 10}
            onChange={(e) => setLayer({ zIndex: Number(e.target.value) })}
          />
        </div>
      </div>

      {/* Type-specific */}
      {layer.type === "image" ? (
        <div className="space-y-2 pt-2 border-t">
          <div className="font-semibold">Ảnh</div>

          <div>
            <div className="label">
              <span className="label-text">Nguồn ảnh nhanh</span>
            </div>
            <Select
              value={layer.sourceKey ?? ""}
              onChange={(e) => setLayer({ sourceKey: e.target.value })}
            >
              {IMAGE_SOURCES.map((x) => (
                <option key={x.key} value={x.key}>
                  {x.label}
                </option>
              ))}
            </Select>
          </div>

          {!(layer.sourceKey || "").trim() && (
            <div>
              <div className="label">
                <span className="label-text">URL ảnh</span>
              </div>
              <Input
                value={layer.src ?? ""}
                onChange={(e) => setLayer({ src: e.target.value })}
                placeholder="https://..."
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="label">
                <span className="label-text">Fit</span>
              </div>
              <Select
                value={layer.fit === "contain" ? "contain" : "cover"}
                onChange={(e) => setLayer({ fit: e.target.value })}
              >
                <option value="cover">Cover</option>
                <option value="contain">Contain</option>
              </Select>
            </div>

            <div className="flex items-end">
              <label className="label cursor-pointer gap-2">
                <span className="label-text">Bo tròn</span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={!!layer.circle}
                  onChange={(e) => {
                    const c = e.target.checked;
                    if (c)
                      setLayer({ circle: true, height: layer.width ?? 20 });
                    else setLayer({ circle: false });
                  }}
                />
              </label>
            </div>
          </div>

          <div className="text-xs text-gray-500 break-words">
            Preview src:{" "}
            <span className="text-gray-700">
              {resolveImageSource(
                layer.sourceKey,
                s,
                MOCK.company,
                MOCK.repUser,
              ) ||
                layer.src ||
                "—"}
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-2 pt-2 border-t">
          <div className="font-semibold">Text</div>

          <div className="p-3 rounded-lg border bg-base-100 space-y-2">
            <div className="font-semibold text-sm">
              Auto điền từ dữ liệu preview
            </div>
            <Select
              value={selectedFieldKey}
              onChange={(e) => setSelectedFieldKey(e.target.value)}
            >
              {FIELD_DEFS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Select>
            <button
              className="btn btn-outline btn-primary w-full"
              onClick={() => applySelectedFromField(selectedFieldKey)}
            >
              Áp dụng vào khối đang chọn
            </button>
          </div>

          <div>
            <div className="label">
              <span className="label-text">Nội dung</span>
            </div>
            <Input
              value={layer.text ?? ""}
              onChange={(e) => setLayer({ text: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="label">
                <span className="label-text">Font size</span>
              </div>
              <Input
                type="number"
                value={Number(st.fontSize ?? 18)}
                onChange={(e) =>
                  setLayer({
                    style: { ...st, fontSize: Number(e.target.value) },
                  })
                }
              />
            </div>

            <div>
              <div className="label">
                <span className="label-text">Font weight</span>
              </div>
              <Select
                value={String(st.fontWeight ?? "400")}
                onChange={(e) =>
                  setLayer({ style: { ...st, fontWeight: e.target.value } })
                }
              >
                {[
                  "100",
                  "200",
                  "300",
                  "400",
                  "500",
                  "600",
                  "700",
                  "800",
                  "900",
                ].map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <div className="label">
                <span className="label-text">Align</span>
              </div>
              <Select
                value={st.align ?? "left"}
                onChange={(e) =>
                  setLayer({ style: { ...st, align: e.target.value } })
                }
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </div>

            <div>
              <div className="label">
                <span className="label-text">Màu chữ</span>
              </div>
              <Input
                value={st.color ?? "#ffffff"}
                onChange={(e) =>
                  setLayer({ style: { ...st, color: e.target.value } })
                }
              />
            </div>

            <div className="col-span-2">
              <div className="label">
                <span className="label-text">Font family</span>
              </div>
              <Input
                value={st.fontFamily ?? FONT_TNR}
                onChange={(e) =>
                  setLayer({ style: { ...st, fontFamily: e.target.value } })
                }
              />
            </div>

            <div className="col-span-2">
              <div className="label">
                <span className="label-text">Line height</span>
              </div>
              <Input
                type="number"
                step="0.05"
                value={Number(st.lineHeight ?? 1.25)}
                onChange={(e) =>
                  setLayer({
                    style: { ...st, lineHeight: Number(e.target.value) },
                  })
                }
              />
            </div>

            <div className="col-span-2">
              <label className="label cursor-pointer gap-2">
                <span className="label-text">Uppercase</span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={!!st.uppercase}
                  onChange={(e) =>
                    setLayer({ style: { ...st, uppercase: e.target.checked } })
                  }
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- API helpers ---------- */
const extractList = (res) => {
  const payload = res?.data?.data ?? res?.data ?? null;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const extractDetail = (res) => res?.data?.data ?? res?.data ?? null;

/* ======================== Main page ======================== */
const WelcomeCardTemplates = () => {
  const canView = hasPermission("WELCOME_CARD_SHOW");
  const canCreate = hasPermission("WELCOME_CARD_CREATE");
  const canUpdate = hasPermission("WELCOME_CARD_UPDATE");
  const canDelete = hasPermission("WELCOME_CARD_DELETE");

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");

  const [confirm, setConfirm] = useState({ open: false, id: null });

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [selected, setSelected] = useState(null);

  const [metaForm, setMetaForm] = useState({
    code: "",
    name: "",
    version: 1,
    isActive: true,
  });

  const [schema, setSchema] = useState(() => ensureSchemaV2(null));

  const loadTemplates = async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {};
      if (search?.trim()) params.s = search.trim();

      const res = await ApiCustom.get("/welcome-card-template-show", {
        params,
      });
      const items = extractList(res);
      setTemplates(Array.isArray(items) ? items : []);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Không tải được danh sách mẫu thiệp",
      );
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openCreate = () => {
    if (!canCreate) return;
    setEditing(null);
    setMetaForm({ code: "", name: "", version: 1, isActive: true });
    setSchema(ensureSchemaV2(null));
    setSelected(null);
    setEditorOpen(true);
  };

  const openEdit = async (item) => {
    if (!canUpdate) return;
    try {
      const res = await ApiCustom.get(
        `/welcome-card-template-show-id/${item.id}`,
      );
      const detail = extractDetail(res) || item;

      setEditing(detail);
      setMetaForm({
        code: detail.code || "",
        name: detail.name || "",
        version: Number(detail.version ?? 1),
        isActive: Boolean(detail.isActive ?? true),
      });

      const loaded = ensureSchemaV2(
        safeParseSchema(safeParseSchema(detail.schema)),
      );
      setSchema(loaded);
      setSelected(null);
      setEditorOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Không lấy được chi tiết mẫu");
    }
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditing(null);
    setSelected(null);
  };

  const saveTemplate = async () => {
    const payload = {
      code: String(metaForm.code || "").trim(),
      name: String(metaForm.name || "").trim(),
      version: Number(metaForm.version) || 1,
      isActive: Boolean(metaForm.isActive),
      schema: JSON.stringify(ensureSchemaV2(schema)),
    };

    if (!payload.code) return toast.error("Vui lòng nhập code");
    if (!payload.name) return toast.error("Vui lòng nhập tên mẫu");

    try {
      if (editing?.id) {
        await ApiCustom.put(
          `/welcome-card-template-update/${editing.id}`,
          payload,
          JSON_HEADERS,
        );
        toast.success("Cập nhật mẫu thiệp thành công");
      } else {
        await ApiCustom.post(
          `/welcome-card-template-create`,
          payload,
          JSON_HEADERS,
        );
        toast.success("Tạo mẫu thiệp thành công");
      }
      closeEditor();
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lỗi khi lưu mẫu thiệp");
    }
  };

  const requestDelete = (id) => {
    if (!canDelete) return;
    setConfirm({ open: true, id });
  };

  const doDelete = async () => {
    if (!canDelete) return;
    try {
      await ApiCustom.delete(
        `/welcome-card-template-delete/${confirm.id}`,
        JSON_HEADERS,
      );
      toast.success("Đã xoá mẫu thiệp");
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Xoá thất bại");
    } finally {
      setConfirm({ open: false, id: null });
    }
  };

  const resetSchema = () => {
    setSchema(ensureSchemaV2(null));
    setSelected(null);
    toast.info("Đã reset về mẫu mặc định");
  };

  const addText = () => {
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      const newLayer = normalizeLayer({
        id: genId("txt"),
        type: "text",
        top: 60,
        left: 10,
        width: 60,
        height: 4,
        zIndex: (next.layers?.length || 0) + 50,
        text: "Text mới",
        style: {
          fontFamily: FONT_TNR,
          fontSize: 18,
          fontWeight: "600",
          color: "#ffffff",
          align: "left",
          uppercase: false,
          lineHeight: 1.25,
        },
      });
      next.layers = [...(next.layers || []), newLayer];
      return next;
    });
    toast.info("Đã thêm Text.");
  };

  const addImage = () => {
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      const newLayer = normalizeLayer({
        id: genId("img"),
        type: "image",
        top: 60,
        left: 10,
        width: 20,
        height: 12,
        zIndex: (next.layers?.length || 0) + 50,
        sourceKey: "",
        src: "",
        fit: "cover",
        circle: false,
      });
      next.layers = [...(next.layers || []), newLayer];
      return next;
    });
    toast.info("Đã thêm Ảnh.");
  };

  const headers = useMemo(() => {
    const base = [
      {
        key: "id",
        label: "STT",
        render: (_, index) => <span>{index + 1}</span>,
      },
      { key: "code", label: "Code" },
      { key: "name", label: "Tên mẫu" },
      { key: "version", label: "Version" },
      {
        key: "isActive",
        label: "Trạng thái",
        render: (item) => (
          <span
            className={`badge ${item?.isActive ? "badge-success" : "badge-ghost"}`}
          >
            {item?.isActive ? "Đang dùng" : "Tắt"}
          </span>
        ),
      },
    ];
    if (canUpdate || canDelete)
      base.push({ key: "setting", label: "Phương thức" });
    return base;
  }, [canUpdate, canDelete]);

  const settings = {
    renderSettings: (item) => (
      <div className="flex items-center gap-2">
        {canUpdate && (
          <button
            onClick={() => openEdit(item)}
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
            title="Sửa"
          >
            <PencilSquareIcon className="w-5 h-5" />
          </button>
        )}
        {canDelete && (
          <button
            onClick={() => requestDelete(item.id)}
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
            title="Xoá"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };

  if (!canView) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Cài đặt thiệp chào mừng</h1>
        <div className="alert alert-warning">
          Bạn không có quyền truy cập chức năng này.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="text-2xl font-bold">Cài đặt thiệp chào mừng</h1>

        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm theo code / tên…"
            className="input input-bordered w-64"
          />
          {canCreate && (
            <button className="btn btn-success" onClick={openCreate}>
              <PlusCircleIcon className="w-5 h-5 mr-1" />
              Thêm mẫu
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500">Đang tải…</div>
      ) : (
        <TableComponent
          headers={headers}
          data={templates}
          settings={settings}
        />
      )}

      <Modal
        isOpen={editorOpen}
        title={editing?.id ? "Chỉnh sửa mẫu thiệp" : "Tạo mẫu thiệp "}
        onClose={closeEditor}
        footer={
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              <button
                className="btn btn-outline"
                onClick={resetSchema}
                title="Reset về mặc định"
              >
                <ArrowPathIcon className="w-5 h-5 mr-1" />
                Reset
              </button>

              <button className="btn btn-outline btn-primary" onClick={addText}>
                <PlusIcon className="w-5 h-5 mr-1" />
                Thêm Text
              </button>

              <button className="btn btn-outline btn-info" onClick={addImage}>
                <PlusIcon className="w-5 h-5 mr-1" />
                Thêm Ảnh
              </button>
            </div>

            <div className="flex gap-2">
              <button className="btn btn-ghost" onClick={closeEditor}>
                Huỷ
              </button>
              <button className="btn btn-primary" onClick={saveTemplate}>
                Lưu
              </button>
            </div>
          </div>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8">
            <div className="p-3 rounded-xl border bg-base-100">
              <TemplateEditorStage
                schema={schema}
                setSchema={setSchema}
                selected={selected}
                setSelected={setSelected}
              />
              <div className="mt-2 text-xs text-gray-500">
                Mẹo: Bấm khung nét đứt để chọn. Giữ/kéo để di chuyển.
              </div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="p-4 rounded-xl border bg-base-100 space-y-4">
              <div className="font-semibold">Thông tin mẫu</div>

              <div className="grid grid-cols-1 gap-2">
                <div>
                  <div className="label">
                    <span className="label-text">Code</span>
                  </div>
                  <Input
                    value={metaForm.code}
                    onChange={(e) =>
                      setMetaForm((s) => ({ ...s, code: e.target.value }))
                    }
                    placeholder="Mã"
                    disabled={!!editing?.id}
                  />
                </div>

                <div>
                  <div className="label">
                    <span className="label-text">Tên mẫu</span>
                  </div>
                  <Input
                    value={metaForm.name}
                    onChange={(e) =>
                      setMetaForm((s) => ({ ...s, name: e.target.value }))
                    }
                    placeholder="Thiệp chào mừng v1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="label">
                      <span className="label-text">Version</span>
                    </div>
                    <Input
                      type="number"
                      min={1}
                      value={metaForm.version}
                      onChange={(e) =>
                        setMetaForm((s) => ({ ...s, version: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="label">
                      <span className="label-text">Trạng thái</span>
                    </div>
                    <Select
                      value={metaForm.isActive ? "1" : "0"}
                      onChange={(e) =>
                        setMetaForm((s) => ({
                          ...s,
                          isActive: e.target.value === "1",
                        }))
                      }
                    >
                      <option value="1">Đang dùng</option>
                      <option value="0">Tắt</option>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <ElementControls
                  schema={schema}
                  setSchema={setSchema}
                  selected={selected}
                  setSelected={setSelected}
                />
              </div>

              <div className="border-t pt-4 text-xs text-gray-500">
                Ghi chú: Width là % theo chiều rộng; Height là % theo chiều cao.
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDeleteModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, id: null })}
        onConfirm={doDelete}
      />
    </div>
  );
};

export default WelcomeCardTemplates;
